/**
 * This file is literally a word-to-word copy of the original JSON schema of Lottie:
 * https://lottiefiles.github.io/lottie-docs/schema/
 *
 * What I mean by this is, the names, grouping, documentation, etc is same as that in the JSON schema.
 * Even the order of the types in this file is same as that in the JSON schema.
 * This is why you may find empty namespaces and types in this file because they are acting as placeholders
 * and help me keep track of JSON schema pending consversion to Typescript.
 *
 * Note: this file is incomplete. We don't use many of the types in Luminary yet.
 * Any missing parts are currently using `any`, `null`, and empty namespaces.
 */
import type { Config } from '../../../video-data/config';

namespace AnimatedProperties {
    export type PositionKeyframe = {};
    export type ShapeProperty = {};

    /**
     * Multi Dimensional
     *
     * An animatable property that holds an array of numbers
     */
    export type AnimatedPropertyBase = {
        /**
         * Property Index
         */
        ix?: number;
        /**
         * Expression
         */
        x?: string;
    };

    type KeyframeBaseWithHold = {
        /**
         * Time
         */
        t: number;
        /**
         * Hold
         */
        h?: 1;
    };

    type KeyframeBaseWithoutHold = {
        /**
         * Time
         */
        t: number;
        /**
         * Hold
         */
        h?: 0;
        /**
         * In Tangent
         *
         * Easing tangent going into the next keyframe
         */
        i?: KeyframeBezierHandle;
        /**
         * Out Tangent
         *
         * Easing tangent leaving the current keyframe
         */
        o?: KeyframeBezierHandle;
    };

    export type KeyframeBase = KeyframeBaseWithHold | KeyframeBaseWithoutHold;

    export type Keyframe = AnimatedProperties.KeyframeBase & {
        /**
         * Value
         *
         * Value at the keyframe. Note that if the property is scalar, keyframe values are still represented as arrays.
         */
        s: number[];
        /**
         * End Value
         *
         * Value at the end of the keyframe, note that this is deprecated and you should use 's' from the next keyframe instead.
         */
        e?: number[];
    };
    export type ShapeKeyframe = {};

    /**
     * Color Value
     *
     * An animatable property that holds a Color
     */
    export type ColorValue = AnimatedColorValue | StaticColorValue;
    type AnimatedColorValue = AnimatedProperties.AnimatedPropertyBase & {
        /**
         * Animated
         *
         * Whether the property is animated
         */
        a: 1;
        /**
         * Animated Value
         *
         * Array of keyframes
         */
        k: AnimatedProperties.Keyframe[];
    };
    type StaticColorValue = AnimatedProperties.AnimatedPropertyBase & {
        /**
         * Animated
         *
         * Whether the property is animated
         */
        a: 0;
        /**
         * Static value
         */
        k: Helpers.Color;
    };

    export type SplitVector = {};

    /**
     * Multi Dimensional
     *
     * An animatable property that holds an array of numbers
     */
    type MultiDimensional_Common = {
        /**
         * Length
         *
         * Number of components in the value arrays.
         * If present values will be truncated or expanded to match this length when accessed from expressions.
         */
        l?: number;
    };
    export type MultiDimensional<T = number[]> =
        | AnimatedMultiDimensional
        | StaticMultiDimensional<T>;
    type AnimatedMultiDimensional = AnimatedProperties.AnimatedPropertyBase & {
        /**
         * Animated
         *
         * Whether the property is animated
         */
        a: 1;
        /**
         * Animated Value
         *
         * Array of keyframes
         */
        k: AnimatedProperties.Keyframe[];
    } & MultiDimensional_Common;
    type StaticMultiDimensional<T> = AnimatedProperties.AnimatedPropertyBase & {
        /**
         * Animated
         *
         * Whether the property is animated
         */
        a: 0;
        /**
         * Static value
         */
        k: T;
    } & MultiDimensional_Common;

    type AnimatedPosition = {
        /**
         * Property Index
         */
        ix?: number;
        /**
         * Animated - whether the property is animated
         */
        a?: 1;
        /**
         * Expression
         */
        x?: string;
        /**
         * Length
         *
         * Number of components in the value arrays.
         * If present, values will be truncated or expanded to match its length when accessed from expressions.
         */
        l?: number;
        /**
         * Animated Value
         * Array of keyframes
         */
        k: Keyframe[];
    };
    type StaticPosition = {
        /**
         * Property Index
         */
        ix?: number;
        /**
         * Animated - whether the property is animated
         */
        a?: 0;
        /**
         * Expression
         */
        x?: string;
        /**
         * Length
         *
         * Number of components in the value arrays.
         * If present, values will be truncated or expanded to match its length when accessed from expressions.
         */
        l?: number;
        /**
         * Static Value
         */
        k: number[];
    };
    export type Position = AnimatedPosition | StaticPosition;

    /**
     * Gradient Colors
     *
     * Represents colors and offsets in a gradient
     *
     * Colors are represented as a flat list interleaving offsets and color components in weird ways
     * There are two possible layouts:
     *
     * Without alpha, the colors are a sequence of offset, r, g, b
     *
     * With alpha, same as above but at the end of the list there is a sequence of offset, alpha
     *
     * Examples:
     *
     * For the gradient [0, red], [0.5, yellow], [1, green]
     * The list would be [0, 1, 0, 0, 0.5, 1, 1, 0, 1, 0, 1, 0]
     *
     * For the gradient [0, red at 80% opacity], [0.5, yellow at 70% opacity], [1, green at 60% opacity]
     * The list would be [0, 1, 0, 0, 0.5, 1, 1, 0, 1, 0, 1, 0, 0, 0.8, 0.5, 0.7, 1, 0.6]
     */
    export type GradientColors =
        | {
              /**
               * Colors
               */
              k: AnimatedProperties.MultiDimensional<[]>;
              /**
               * Count
               *
               * Number of colors in `k`
               */
              p: 0;
          }
        | {
              /**
               * Colors
               */
              k: AnimatedProperties.MultiDimensional<Helpers.GradientColor>;
              /**
               * Count
               *
               * Number of colors in `k`
               *
               * Note this type only defines up to 9, but can have more. Typescript doesn't have a
               * nice way for typing non-zero integers.
               */
              p: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
          };

    /**
     * Keyframe Bezier Handle
     *
     * Bezier handle for keyframe interpolation
     */
    export type KeyframeBezierHandle = {
        /**
         * X
         *
         * Time component:
         * 0 means start time of the keyframe,
         * 1 means time of the next keyframe.
         */
        x: number[] | number;
        /**
         * Y
         *
         * Value interpolation component:
         * 0 means start value of the keyframe,
         * 1 means value at the next keyframe.
         */
        y: number[] | number;
    };

    /**
     * Value
     *
     * An animatable property that holds a float
     */
    export type Value = AnimatedValue | StaticValue;
    type AnimatedValue = AnimatedProperties.AnimatedPropertyBase & {
        /**
         * Animated
         *
         * Whether the property is animated
         */
        a: 1;
        /**
         * Animated Value
         *
         * Array of keyframes
         */
        k: AnimatedProperties.Keyframe[];
    };
    type StaticValue = AnimatedProperties.AnimatedPropertyBase & {
        /**
         * Animated
         *
         * Whether the property is animated
         */
        a: 0;
        /**
         * Static value
         */
        k: number;
    };
}

namespace Animation {
    /**
     * Motion Blur
     *
     * Motion blur settings
     */
    export type MotionBlur = {
        /**
         * Shutter Angle
         *
         * Angle in degrees
         */
        sa: number;
        /**
         * Shutter Phase
         *
         * Angle in degrees
         */
        sp: number;
        /**
         * Samples per Frame
         */
        spf: number;
        /**
         * Adaptive Sample Limit
         */
        asl: number;
    };

    /**
     * Composition
     *
     * Base class for layer holders
     */
    export type Composition = {
        /**
         * Layers
         */
        layers: DerivedTypes.Layer[];
    };

    /**
     * Animation
     *
     * Top level object, describing the animation
     */
    export type Animation = Helpers.VisualObject & {
        /**
         * Version
         */
        v?: string;
        /**
         * Framerate
         *
         * Framerate in frames per second
         */
        fr: number;
        /**
         * In Point
         *
         * \"In Point\", which frame the animation starts at (usually 0)
         */
        ip: number;
        /**
         * Out Point
         *
         * \"Out Point\", which frame the animation stops/loops at, which makes this the duration in frames when `ip` is 0
         */
        op: number;
        /**
         * Width
         *
         * Width of the animation
         */
        w: number;
        /**
         * Height
         *
         * Height of the animation
         */
        h: number;
        /**
         * Threedimensional
         *
         * Whether the animation has 3D layer
         */
        ddd?: Helpers.IntBoolean;
        /**
         * Assets
         *
         * List of assets that can be referenced by layers
         */
        assets?: DerivedTypes.Asset[];
        /**
         * Extra Compositions
         *
         * List of Extra compositions not referenced by anything
         */
        comps?: Assets.Precomposition[];
        /**
         * Fonts
         */
        fonts?: Text.FontList;
        /**
         * Chars
         *
         * Data defining text characters as lottie shapes. If present a player might only render characters defined here and nothing else.
         */
        chars?: Text.CharacterData[];
        /**
         * Metadata
         *
         * Document metadata
         */
        meta?: Animation.Metadata;
        /**
         * User Metadata
         *
         * User-defined metadat
         */
        metadata?: Object;
        /**
         * Markers
         *
         * Markers defining named sections of the composition.
         */
        markers?: Helpers.Marker[];
        /**
         * Motion Blur
         */
        mb?: Animation.MotionBlur;
    } & Animation.Composition;

    /**
     * Metadata
     *
     * Document metadata
     */
    export type Metadata = {
        /**
         * Author
         */
        a?: string;
        /**
         * Description
         */
        d?: string;
        /**
         * Theme Color
         */
        tc?: string;
        /**
         * Generator
         *
         * Software used to generate the file
         */
        g?: string;
        /**
         * Keywords
         */
        k?: string | string[];
    };
}

namespace Assets {
    export type DataSource = Assets.FileAsset & {};

    /**
     * Asset
     */
    export type Asset = {
        /**
         * ID
         *
         * Unique identifier used by layers when referencing this asset
         */
        id: string;
        /**
         * Name
         *
         * Human readable name
         */
        nm?: string;
    };

    export type Sound = Assets.FileAsset;

    /**
     * Precomposition
     *
     * Asset containing an animation that can be referenced by layers.
     */
    export type Precomposition = Assets.Asset &
        Animation.Composition & {
            /**
             * Framerate
             *
             * Framerate in frames per second
             */
            fr?: number;
            /**
             * Extra
             *
             * Extra composition
             */
            xt?: Helpers.IntBoolean;
        };

    export type Image = Assets.FileAsset & {};
    export type FileAsset = Assets.Asset & {};
}

namespace Constants {
    export type MatteMode = MatteModeType;

    export type Composite = null;
    export type FontPathOrigin = null;
    export type LineCap = null;
    export type TextBase = null;
    export type MaskMode = null;

    /**
     * FillRule
     *
     * Rule used to handle multiple shapes rendered with the same fill object
     */
    export enum FillRule {
        /**
         * Non Zero
         *
         * Everything is colored (You can think of this as an OR)
         */
        NonZero = 1,
        /**
         * Even Odd
         *
         * Colored based on intersections and path direction, can be used to create \"holes\"
         */
        EvenOdd = 2,
    }

    export type LineJoin = null;
    export type TextJustify = null;
    export type TextCaps = null;
    export type BlendMode = null;
    export type TextShape = null;

    /**
     * ShapeDirection
     *
     * Drawing direction of the shape curve, useful for trim path
     */
    export enum ShapeDirection {
        /**
         * Normal
         *
         * Usually clockwise
         */
        Normal = 1,
        /**
         * Reversed
         *
         * Usually counter clockwise
         */
        Reversed = 3,
    }

    export type StrokeDashType = null;
    export type TextGrouping = null;
    export enum GradientType {
        /**
         * Linear
         */
        Linear = 1,
        /**
         * Radial
         */
        Radial = 2,
    }
    export type StarType = null;
    export type TrimMultipleShapes = null;
}

namespace EffectValues {
    /**
     * Value for an effect
     */
    export type EffectValue = Helpers.VisualObject & {
        /**
         * Effect index
         */
        ix?: number;
    };

    /**
     * Effect value slider
     */
    export type Slider = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Slider;
        /**
         * Value
         */
        v?: AnimatedProperties.Value;
    };

    /**
     * Effect value angle
     */
    export type Angle = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Angle;
        /**
         * Value
         */
        v?: AnimatedProperties.Value;
    };

    /**
     * Effect value color
     */
    export type Color = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Color;
        /**
         * Value
         */
        v?: AnimatedProperties.ColorValue;
    };

    /**
     * Effect value point
     */
    export type Point = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Point;
        /**
         * Value
         */
        v?: AnimatedProperties.MultiDimensional;
    };

    /**
     * Effect value checkbox
     */
    export type Checkbox = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Checkbox;
        /**
         * Value
         */
        v?: AnimatedProperties.Value;
    };

    /**
     * Effect value ignored
     */
    export type Ignored = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Ignored;
        /**
         * Value
         */
        v?: 0;
    };

    /**
     * Effect value drop down
     */
    export type DropDown = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.DropDown;
        /**
         * Value
         */
        v?: AnimatedProperties.Value;
    };

    /**
     * Effect value layer
     */
    export type Layer = EffectValue & {
        /**
         * Type
         */
        ty: EffectValueType.Angle;
        /**
         * Value
         */
        v?: AnimatedProperties.Value;
    };

    /**
     * Effect no value
     */
    export type NoValue = null;
}

namespace Effects {
    /**
     * Effect
     */
    type Effect = Helpers.VisualObject & {
        /**
         * Effect index
         */
        ix?: number;
        /**
         * Enabled
         */
        en?: Helpers.IntBoolean;
        /**
         * Effect type
         */
        ty: EffectType;
        /**
         * Effect values
         */
        ef: DerivedTypes.EffectValue[];
    };
    /**
     * Custom Effect
     *
     * Some lottie files use ty = 5 for many different effects
     */
    export type CustomEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.CustomEffect;
    };
    /**
     * Paint over transparent effect
     */
    export type PaintOverTransparentEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.PaintOverTransparent;
        /**
         * Effect values
         *
         * Slider - 00
         */
        ef: [EffectValues.Slider];
    };
    /**
     * Tint effect
     *
     * Colorizes the layer
     */
    export type TintEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Tint;
        /**
         * Effect values
         *
         * Color - Black Color: Tint of the darker parts of the layer
         * Color - White Color: Tint of the lighter parts of the layer
         * Slider - Intensity: Intensity of the effect, 0 means the layer is unchanged. 100
         * means full effect
         */
        ef: [EffectValues.Color, EffectValues.Color, EffectValues.Slider];
    };
    /**
     * Fill effect
     *
     * Replaces the whole layer with the given color
     */
    export type FillEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Fill;
        /**
         * Effect values
         *
         * Point - 00
         * DropDown - 01
         * Color - color
         * DropDown - 03
         * Slider - 04
         * Slider - 05
         * Slider - opacity: opacity in [0,1]
         */
        ef: [
            EffectValues.Point,
            EffectValues.DropDown,
            EffectValues.Color,
            EffectValues.DropDown,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
        ];
    };
    /**
     * Stroke effect
     */
    export type StrokeEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Stroke;
        /**
         * Effect values
         *
         * Color - 00
         * Checkbox - 01
         * Checkbox - 02
         * Color - color
         * Slider - 04
         * Slider - 05
         * Slider - 06
         * Slider - 07
         * Slider - 08
         * Dropdown - 09
         * Dropdown - type
         */
        ef: [
            EffectValues.Color,
            EffectValues.Checkbox,
            EffectValues.Checkbox,
            EffectValues.Color,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.DropDown,
            EffectValues.DropDown,
        ];
    };
    /**
     * Tritone effect
     *
     * Maps layers colors based on bright/mid/dark colors
     */
    export type TritoneEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Tritone;
        /**
         * Effect values
         *
         * Color - bright
         * Color - mid
         * Color - dark
         */
        ef: [EffectValues.Color, EffectValues.Color, EffectValues.Color];
    };
    /**
     * Pro levels effect
     */
    export type ProLevelsEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.ProLevels;
        /**
         * Effect values
         *
         * DropDown - 00
         * NoValue - 01
         * NoValue - 02
         * Slider - comp in black
         * Slider - comp in white
         * Slider - comp gamma
         * Slider - comp out black
         * NoValue - comp out white
         * NoValue - 08
         * Slider - 09
         * Slider - r in black
         * Slider - r in white
         * Slider - r gamma
         * Slider - r out black
         * Slider - r out white
         * Slider - 15
         * Slider - 16
         * Slider - g in black
         * Slider - g in white
         * Slider - g gamma
         * Slider - g out black
         * Slider - g out white
         * Slider - 22
         * Slider - 23
         * Slider - b in black
         * Slider - b in white
         * Slider - b gamma
         * Slider - b out black
         * Slider - b out white
         * Slider - 29
         * Slider - a in black
         * Slider - a in white
         * Slider - a gamma
         * Slider - a out black
         * NoValue - a out white
         */
        ef: [
            EffectValues.DropDown,
            EffectValues.NoValue,
            EffectValues.NoValue,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.NoValue,
            EffectValues.NoValue,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.NoValue,
        ];
    };
    /**
     * Drop shadow effect
     *
     * Adds a shadow to the layer
     */
    export type DropShadowEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.DropShadow;
        /**
         * Effect values
         *
         * Color - color
         * Slider - opacity
         * Angle - angle
         * Slider - distance
         * Slider - blur
         */
        ef: [
            EffectValues.Color,
            EffectValues.Slider,
            EffectValues.Angle,
            EffectValues.Slider,
            EffectValues.Slider,
        ];
    };
    /**
     * Radial wipe effect
     */
    export type RadialWipeEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.RadialWipe;
        /**
         * Effect values
         *
         * Slider - completion: between 0 and 100
         * Angle - start angle
         * Point - wipe center
         * Slider - wipe
         * Slider - feather
         */
        ef: [
            EffectValues.Slider,
            EffectValues.Angle,
            EffectValues.Point,
            EffectValues.Slider,
            EffectValues.Slider,
        ];
    };
    /**
     * Displacement map effect
     */
    export type DisplacementMapEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.DisplacementMap;
        /**
         * Effect values
         *
         * Layer - displacement map layer
         * DropDown - use for horizontal displacement
         * Slider - max horizontal displacement
         * DropDown - use for vertical displacement
         * Slider - max vertical displacement
         * DropDown - displacement map behaviour
         * DropDown - edge behaviour
         * DropDown - expand output
         */
        ef: [
            EffectValues.Layer,
            EffectValues.DropDown,
            EffectValues.Slider,
            EffectValues.DropDown,
            EffectValues.Slider,
            EffectValues.DropDown,
            EffectValues.DropDown,
            EffectValues.DropDown,
        ];
    };
    /**
     * Matte effect
     */
    export type Matte3Effect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Matte3;
        /**
         * Effect values
         *
         * Layer - layer: use this layer as a mask
         * DropDown - channel: channel to use as a mask: 1 - Red, 2 - Green, 3 - Blue, 4 - Alpha, 5 - Luminance 6, - Hue, 7 - Lightness, 8 - Saturation, 9 - Full, 10 - Off
         * DropDown - invert: use 0 as opaque value when true
         * DropDown - stretch to fit
         * DropDown - show mask: if false, the mask layer won't be shown
         * DropDown - premultiply mask
         */
        ef: [
            EffectValues.Layer,
            EffectValues.DropDown,
            EffectValues.DropDown,
            EffectValues.DropDown,
            EffectValues.DropDown,
            EffectValues.DropDown,
        ];
    };
    /**
     * Gaussian blur effect
     */
    export type GaussianBlurEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.GaussianBlur;
        /**
         * Effect values
         *
         * Slider - sigma
         * Slider - direction
         * CheckBox - wrap
         */
        ef: [EffectValues.Slider, EffectValues.Slider, EffectValues.Checkbox];
    };
    /**
     * Mesh warp effect
     */
    export type MeshWarpEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.MeshWarp;
        /**
         * Effect values
         *
         * Slider - rows
         * Slider - columns
         * Slider - quality
         * NoValue - 03
         */
        ef: [EffectValues.Slider, EffectValues.Slider, EffectValues.Slider, EffectValues.NoValue];
    };
    /**
     * Wavy effect
     */
    export type WavyEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Wavy;
        /**
         * Effect values
         *
         * Slider - radius
         * Point - center
         * DropDown - conversion type
         * DropDown - speed
         * Slider - width
         * Slider - height
         * Slider - phase
         */
        ef: [
            EffectValues.Slider,
            EffectValues.Point,
            EffectValues.DropDown,
            EffectValues.DropDown,
            EffectValues.Slider,
            EffectValues.Slider,
            EffectValues.Slider,
        ];
    };
    /**
     * Spherize effect
     */
    export type SpherizeEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Spherize;
        /**
         * Effect values
         *
         * Slider - Radius,
         * Point - Center
         */
        ef: [EffectValues.Slider, EffectValues.Point];
    };
    /**
     * Puppet effect
     */
    export type PuppetEffect = Effect & {
        /**
         * Effect type
         */
        ty: EffectType.Puppet;
        /**
         * Effect values
         *
         * DropDown - puppet engine,
         * Slider - mesh rotation refinement
         * DropDown - on transparent
         * NoValue - 03
         */
        ef: [
            EffectValues.DropDown,
            EffectValues.Slider,
            EffectValues.DropDown,
            EffectValues.NoValue,
        ];
    };
}

namespace Helpers {
    /**
     * Color
     *
     * Color as a [r, g, b] array with values in [0, 1]
     */
    export type Color = [number, number, number, number?];

    export type GradientColor = [number, number, number, number, ...number[]];

    export type Mask = {};

    export type Transform = {
        o?: AnimatedProperties.Value;
        r?: AnimatedProperties.Value;
        p?: AnimatedProperties.Position;
        a?: AnimatedProperties.Position;
        s?: AnimatedProperties.MultiDimensional;
    };

    export type Marker = {};

    /**
     * Integer Boolean
     *
     * Represents boolean values as an integer. 0 is false, 1 is true.
     */
    export type IntBoolean = DerivedTypes.IntBoolean;

    /**
     * Bezier
     *
     * Single bezier curve
     */
    export type Bezier = {
        /**
         * Closed
         *
         * Whether the bezier forms a closed loop
         */
        c: 0 | 1;

        /**
         * Vertices
         *
         * Array of points, each point is an array of coordinates.
         * These points are along the bezier path
         */
        v: Array<[number, number]>;

        /**
         * In Tangents
         *
         * Array of points, each point is an array of coordinates.
         * These points are along the `in` tangents relative to the corresponding `v`.
         */
        i: Array<[number, number]>;

        /**
         * Out Tangents
         *
         * Array of points, each point is an array of coordinates.
         * These points are along the `out` tangents relative to the corresponding `v`.
         */
        o: Array<[number, number]>;
    };

    /**
     * Visual Object
     */
    export type VisualObject = {
        /**
         * Name
         *
         * Name, as seen from editors and the like
         */
        nm?: string;
        /**
         * Match Name
         *
         * Match name, used in expression
         */
        mn?: string;
    };
}

namespace Layers {
    export type ImageLayer = {};

    /**
     * Solid Color Layer
     *
     * Layer with a solid color rectangle
     */
    export type SolidColorLayer = Layers.VisualLayer & {
        /**
         * Type
         *
         * Layer type
         */
        ty: LayerType.SolidColor;
        /**
         * Color
         *
         * Color of the layer, unlike most other places, the color is a `#rrggbb` hex string
         */
        sc: string;
        /**
         * Height
         */
        sh: number;
        /**
         * Width
         */
        sw: number;
    };

    export type DataLayer = {};
    export type AudioLayer = {};

    /**
     * Shape Layer
     *
     * Layer containing Shapes
     */
    export type ShapeLayer = Layers.VisualLayer & {
        /**
         * Type
         *
         * Layer type
         */
        ty: LayerType.Shape;
        /**
         * Shape List
         *
         * List of valid shapes
         */
        shapes: Shapes.ShapeList;
    };

    /**
     * Layer
     */
    export type Layer = Helpers.VisualObject & {
        /**
         * Threedimensional
         *
         * Whether the layer is threedimensional
         */
        ddd?: Helpers.IntBoolean;
        /**
         * Hidden
         *
         * Whether the layer is hidden
         */
        hd?: boolean;
        /**
         * Type
         *
         * Layer Type
         */
        ty: LayerType;
        /**
         * Index
         *
         * Index that can be used for parenting and referenced in expression
         */
        ind?: number;
        /**
         * Parent Index
         *
         * Must be the `ind` property of another layer
         */
        parent?: number;
        /**
         * Time Stretch
         */
        sr?: number;
        /**
         * In Point
         *
         * Frame when the layers becomes visible
         */
        ip: number;
        /**
         * Out Point
         *
         * Frame when the layers becomes invisible
         */
        op: number;
        /**
         * Start Time
         */
        st: number;
        /**
         * Blend Mode
         */
        bm?: Constants.BlendMode;
        /**
         * CSS Class
         *
         * CSS class used by the SVG renderer
         */
        cl?: string;
        /**
         * Layer XML ID
         *
         * `id` attribute used by the SVG renderer
         */
        ln?: string;
        /**
         * Layer XML tag name
         *
         * tag name used by the SVG renderer
         */
        tg?: string;
    };

    export type AudioSettings = {};
    export type CameraLayer = {};

    /**
     * Precomposition Layer
     *
     * Layer that renders a Precomposition asset
     */
    export type PrecompositionLayer = Layers.VisualLayer & {
        /**
         * Type
         *
         * Layer type
         */
        ty: LayerType.Precomposition;
        /**
         * Reference Id
         *
         * ID of the precomp as specified in the assets
         */
        refId: string;
        /**
         * Width
         */
        w?: number;
        /**
         * Height
         */
        h?: number;
        /**
         * Time Remapping
         */
        tm?: AnimatedProperties.Value;
    };

    export type TextLayer = {};

    /**
     * Visual Layer
     *
     * Layer used to affect visual elements
     */
    export type VisualLayer = Layers.Layer & {
        /**
         * Collapse Transform
         */
        cp?: boolean;
        /**
         * Transform
         *
         * Layer transform
         */
        ks: Helpers.Transform;
        /**
         * Auto Orient
         *
         * If 1, The layer will rotate itself to match its animated position path
         */
        ao?: Helpers.IntBoolean;
        /**
         * Matte Mode
         */
        tt?: Constants.MatteMode;
        /**
         * Matte Target
         */
        td?: number;
        /**
         * Has Masks
         *
         * Whether the layer has masks applied
         */
        hasMask?: boolean;
        /**
         * Masks
         */
        masksProperties?: Helpers.Mask[];
        /**
         * Effects
         *
         * List of layer effects
         */
        ef?: DerivedTypes.Effect[];
        /**
         * Motion Blur
         *
         * Whether motion blur is enabled for the layer
         */
        mb?: boolean;
        /**
         * Layer style
         *
         * Styling effects for this layer
         */
        sy?: DerivedTypes.Style[];
    };

    export type NullLayer = VisualLayer & { ty: LayerType.Null };
}

namespace Shapes {
    /**
     * Shape
     *
     * Drawable shape
     */
    export type Shape = Shapes.ShapeElement & {
        /**
         * Direction
         *
         * Direction the shape is drawn as, mostly relevant when using trim path
         */
        d?: Constants.ShapeDirection;
    };

    export type PuckerBloat = {};
    export type RepeaterTransform = {};

    export type Path = Shapes.ShapeElement & {
        /**
         * Shape Type
         */
        ty: ShapeType.Path;
        /**
         * Bezier path
         */
        ks: Helpers.Bezier;
    };

    export type Rectangle = {};

    /**
     * Gradient
     */
    export type Gradient = {
        /**
         * Start Point
         */
        s: AnimatedProperties.MultiDimensional;
        /**
         * End Point
         */
        e: AnimatedProperties.MultiDimensional;
        /**
         * Gradient Type
         */
        t?: Constants.GradientType;
        /**
         * Highlight Length
         */
        h?: AnimatedProperties.Value;
        /**
         * Highlight Angle
         */
        a?: AnimatedProperties.Value;
        /**
         * Colors
         */
        g: AnimatedProperties.GradientColors;
    };

    export type Polystar = {};
    export type Trim = {};
    export type ZigZag = {};

    /**
     * Stroke
     *
     * Solid stroke
     */
    export type Stroke = Shapes.ShapeElement &
        Shapes.BaseStroke & {
            /**
             * Shape Type
             */
            ty: ShapeType.Stroke;
            /**
             * Color
             */
            c: AnimatedProperties.ColorValue;
        };

    /**
     * Shape Element
     *
     * Base class for all elements of ShapeLayer and Group
     */
    export type ShapeElement = Helpers.VisualObject & {
        /**
         * Hidden
         *
         * Whether the shape is hidden
         */
        hd?: boolean;
        /**
         * Shape Type
         */
        ty: ShapeType;
        /**
         * Blend Mode
         */
        bm?: Constants.BlendMode;
        /**
         * Property index
         *
         * Index used in expressions
         */
        ix?: number;
        /**
         * CSS Class
         *
         * CSS class used by the SVG renderer
         */
        cl?: string;
        /**
         * Layer XML ID
         *
         * `id` attribute used by the SVG renderer
         */
        ln?: string;
    };

    /**
     * Fill
     *
     * Solid fill color
     */
    export type Fill = Shapes.ShapeElement & {
        /**
         * Shape Type
         */
        ty: ShapeType.Fill;
        /**
         * Opacity
         */
        o: AnimatedProperties.Value;
        /**
         * Color
         */
        c: AnimatedProperties.ColorValue;
        /**
         * Fill Rule
         */
        r?: Constants.FillRule;
    };

    export type Transform = {};
    export type Ellipse = {};
    export type Modifier = {};
    export type StrokeDash = {};

    /**
     * Group
     *
     * Shape Element that can contain other shapes
     */
    export type Group = Shapes.ShapeElement & {
        /**
         * Shape Type
         */
        ty: ShapeType.Group;
        /**
         * Number Of Properties
         */
        np?: number;
        /**
         * Shapes
         */
        it?: Shapes.ShapeList;
        /**
         * Property index
         *
         * Index used in expressions
         */
        cix?: number;
    };

    export type Twist = {};

    /**
     * Gradient Fill
     *
     * Gradient fill
     */
    export type GradientFill = Shapes.ShapeElement &
        Shapes.Gradient & {
            /**
             * Shape Type
             */
            ty: ShapeType.GradientFill;
            /**
             * Opacity
             */
            o: AnimatedProperties.Value;
            /**
             * Fill Rule
             */
            r?: Constants.FillRule;
        };

    export type Merge = {};
    export type RoundedCorners = {};

    /**
     * Base Stroke
     */
    export type BaseStroke = {
        /**
         * Line Cap
         */
        lc?: Constants.LineCap;
        /**
         * Line Join
         */
        lj?: Constants.LineJoin;
        /**
         * Miter Limit
         */
        ml?: number;
        /**
         * Miter Limit
         *
         * Animatable alternative to ml
         */
        ml2?: AnimatedProperties.Value;
        /**
         * Opacity
         */
        o: AnimatedProperties.Value;
        /**
         * Width
         */
        w: AnimatedProperties.Value;
        /**
         * Dashes
         */
        d?: Shapes.StrokeDash[];
    };

    export type OffsetPath = {};

    /**
     * Shape List
     *
     * List of valid shapes
     */
    export type ShapeList = DerivedTypes.Shape[];

    export type Repeater = {};

    /**
     * Gradient Stroke
     *
     * Gradient stroke
     */
    export type GradientStroke = Shapes.ShapeElement &
        Shapes.BaseStroke &
        Shapes.Gradient & {
            /**
             * Shape Type
             */
            ty: ShapeType.GradientStroke;
        };
}

namespace Styles {
    /**
     * Layer stroke
     */
    export type StrokeStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.Stroke;
        /**
         * Size
         */
        s?: AnimatedProperties.Value;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
    };
    /**
     * Drop shadow
     */
    export type DropShadowStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.DropShadow;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
        /**
         * Opacity
         */
        o?: AnimatedProperties.Value;
        /**
         * Angle
         *
         * Local light angle
         */
        a?: AnimatedProperties.Value;
        /**
         * Size
         *
         * Blur size
         */
        s?: AnimatedProperties.Value;
        /**
         * Distance
         */
        d?: AnimatedProperties.Value;
        /**
         * Choke spread
         */
        ch?: AnimatedProperties.Value;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Noise
         */
        no?: AnimatedProperties.Value;
        /**
         * Layer conceal
         *
         * Layer knock out drop shadow
         */
        lc?: AnimatedProperties.Value;
    };
    /**
     * Inner shadow
     */
    export type InnerShadowStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.InnerShadow;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
        /**
         * Opacity
         */
        o?: AnimatedProperties.Value;
        /**
         * Angle
         *
         * Local light angle
         */
        a?: AnimatedProperties.Value;
        /**
         * Size
         *
         * Blur size
         */
        s?: AnimatedProperties.Value;
        /**
         * Distance
         */
        d?: AnimatedProperties.Value;
        /**
         * Choke spread
         */
        ch?: AnimatedProperties.Value;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Noise
         */
        no?: AnimatedProperties.Value;
    };
    /**
     * Outer glow
     */
    export type OuterGlowStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.OuterGlow;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
        /**
         * Opacity
         */
        o?: AnimatedProperties.Value;
        /**
         * Range
         */
        r?: AnimatedProperties.Value;
        /**
         * Choke spread
         */
        ch?: AnimatedProperties.Value;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Noise
         */
        no?: AnimatedProperties.Value;
        /**
         * Jitter
         */
        j?: AnimatedProperties.Value;
    };
    /**
     * Inner glow
     */
    export type InnerGlowStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.InnerGlow;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
        /**
         * Opacity
         */
        o?: AnimatedProperties.Value;
        /**
         * Range
         */
        r?: AnimatedProperties.Value;
        /**
         * Source
         */
        sr?: AnimatedProperties.Value;
        /**
         * Choke spread
         */
        ch?: AnimatedProperties.Value;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Noise
         */
        no?: AnimatedProperties.Value;
        /**
         * Jitter
         */
        j?: AnimatedProperties.Value;
    };
    /**
     * Bevel emboss
     */
    export type BevelEmbossStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.BevelEmboss;
        /**
         * Bevel style
         */
        bs?: AnimatedProperties.Value;
        /**
         * Technique
         */
        bt?: AnimatedProperties.Value;
        /**
         * Strength
         */
        sr?: AnimatedProperties.Value;
        /**
         * Size
         */
        s?: AnimatedProperties.Value;
        /**
         * Soften
         */
        sf?: AnimatedProperties.Value;
        /**
         * Global angle
         *
         * use global light
         */
        ga?: AnimatedProperties.Value;
        /**
         * Angle
         *
         * local lighting angle
         */
        a?: AnimatedProperties.Value;
        /**
         * Altitude
         *
         * local lighting altitude
         */
        ll?: AnimatedProperties.Value;
        /**
         * Highlight mode
         */
        hm?: AnimatedProperties.Value;
        /**
         * Highlight color
         */
        hc?: AnimatedProperties.ColorValue;
        /**
         * Highlight opacity
         */
        ho?: AnimatedProperties.Value;
        /**
         * Shadow mode
         */
        sm?: AnimatedProperties.Value;
        /**
         * Shadow color
         */
        sc?: AnimatedProperties.ColorValue;
        /**
         * Shadow opacity
         */
        so?: AnimatedProperties.Value;
    };
    /**
     * Satin
     */
    export type SatinStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.Satin;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
        /**
         * Opacity
         */
        o?: AnimatedProperties.Value;
        /**
         * Angle
         */
        a?: AnimatedProperties.Value;
        /**
         * Distance
         */
        d?: AnimatedProperties.Value;
        /**
         * Size
         */
        s?: AnimatedProperties.Value;
        /**
         * Invert
         */
        in?: AnimatedProperties.Value;
    };
    /**
     * Color overlay
     */
    export type ColorOverlayStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.ColorOverlay;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Color
         */
        c?: AnimatedProperties.ColorValue;
        /**
         * Opacity
         */
        so?: AnimatedProperties.Value;
    };
    /**
     * Gradient overlay
     */
    export type GradientOverlayStyle = Helpers.VisualObject & {
        /**
         * Effect type
         */
        ty: StyleType.GradientOverlay;
        /**
         * Blend mode
         */
        bm?: AnimatedProperties.Value;
        /**
         * Opacity
         */
        o?: AnimatedProperties.Value;
        /**
         * Gradient
         */
        gf?: AnimatedProperties.GradientColors;
        /**
         * Smoothness
         */
        gs?: AnimatedProperties.Value;
        /**
         * Angle
         */
        a?: AnimatedProperties.Value;
        /**
         * Gradient type
         */
        gt?: Constants.GradientType;
        /**
         * Reverse
         */
        re?: AnimatedProperties.Value;
        /**
         * Align
         */
        al?: AnimatedProperties.Value;
        /**
         * Scale
         */
        s?: AnimatedProperties.Value;
        /**
         * Offset
         */
        of?: AnimatedProperties.Value;
    };
}

namespace Text {
    export type TextDocument = {};
    export type FontList = {};
    export type CharacterData = {};
    export type TextAnimatorData = {};
    export type TextSelector = {};
    export type Font = {};
    export type TextData = {};
    export type TextSelectorProperty = {};
    export type textDatakeyframe = {};
    export type TextAnimatorDataProperty = {};
    export type TextMoreOptions = {};
    export type MaskedPath = {};
}

namespace DerivedTypes {
    export type IntBoolean = IntBoolean.True | IntBoolean.False;
    export type Layer =
        | Layers.PrecompositionLayer
        | Layers.SolidColorLayer
        | Layers.ShapeLayer
        | Layers.NullLayer;
    export type Shape =
        | Shapes.Fill
        | Shapes.GradientFill
        | Shapes.GradientStroke
        | Shapes.Group
        | Shapes.Stroke
        | Shapes.Path;
    export type Asset = Assets.Image | Assets.Precomposition | Assets.Sound | Assets.DataSource;
    export type EffectValue =
        | EffectValues.NoValue
        | EffectValues.Slider
        | EffectValues.Angle
        | EffectValues.Color
        | EffectValues.Point
        | EffectValues.Checkbox
        | EffectValues.Ignored
        | EffectValues.DropDown
        | EffectValues.Layer;
    export type Effect =
        | Effects.CustomEffect
        | Effects.PaintOverTransparentEffect
        | Effects.TintEffect
        | Effects.FillEffect
        | Effects.StrokeEffect
        | Effects.TritoneEffect
        | Effects.ProLevelsEffect
        | Effects.DropShadowEffect
        | Effects.RadialWipeEffect
        | Effects.DisplacementMapEffect
        | Effects.Matte3Effect
        | Effects.GaussianBlurEffect
        | Effects.MeshWarpEffect
        | Effects.WavyEffect
        | Effects.SpherizeEffect
        | Effects.PuppetEffect;
    export type Style =
        | Styles.StrokeStyle
        | Styles.DropShadowStyle
        | Styles.InnerShadowStyle
        | Styles.OuterGlowStyle
        | Styles.InnerGlowStyle
        | Styles.BevelEmbossStyle
        | Styles.SatinStyle
        | Styles.ColorOverlayStyle
        | Styles.GradientOverlayStyle;
}

export enum LayerType {
    Precomposition = 0,
    SolidColor = 1,
    Image = 2,
    Null = 3,
    Shape = 4,
    Text = 5,
    Audio = 6,
    VideoPlaceholder = 7,
    ImageSequence = 8,
    Video = 9,
    ImagePlaceholder = 10,
    Guide = 11,
    Adjustment = 12,
    Camera = 13,
    Light = 14,
    Data = 15,
}

export enum EffectValueType {
    Slider = 0,
    Angle = 1,
    Color = 2,
    Point = 3,
    Checkbox = 4,
    Ignored = 6,
    DropDown = 7,
    Layer = 10,
}

export enum EffectType {
    CustomEffect = 5,
    PaintOverTransparent = 7,
    Tint = 20,
    Fill = 21,
    Stroke = 22,
    Tritone = 23,
    ProLevels = 24,
    DropShadow = 25,
    RadialWipe = 26,
    DisplacementMap = 27,
    Matte3 = 28,
    GaussianBlur = 29,
    MeshWarp = 31,
    Wavy = 32,
    Spherize = 33,
    Puppet = 34,
}

export enum ShapeType {
    Rectangle = 'rc',
    Ellipse = 'el',
    PolygonStar = 'sr',
    Path = 'sh',
    Fill = 'fl',
    Stroke = 'st',
    GradientFill = 'gf',
    GradientStroke = 'gs',
    Group = 'gr',
    Transform = 'tr',
    RoundedCorners = 'rd',
    PuckerBloat = 'pb',
    Merge = 'mm',
    Twist = 'tw',
    OffsetPath = 'op',
    ZigZag = 'zz',
    Repeater = 'rp',
    Trim = 'tm',
}

export enum StyleType {
    Stroke = 0,
    DropShadow = 1,
    InnerShadow = 2,
    OuterGlow = 3,
    InnerGlow = 4,
    BevelEmboss = 5,
    Satin = 6,
    ColorOverlay = 7,
    GradientOverlay = 8,
}

/**
 * MatteMode
 *
 * How a layer should mask another layer
 */
export enum MatteModeType {
    /**
     * Normal
     */
    Normal = 0,
    /**
     * Alpha
     */
    Alpha = 1,
    /**
     * Inverted Alpha
     */
    InvertedAlpha = 2,
    /**
     * Luma
     */
    Luma = 3,
    /**
     * Inverted Luma
     */
    InvertedLuma = 4,
}

export enum IntBoolean {
    False = 0,
    True = 1,
}

export type Color = Helpers.Color;
export type GradientColor = Helpers.GradientColor;
export type Style = DerivedTypes.Style;
export type Effect = DerivedTypes.Effect;
export type Layer = DerivedTypes.Layer;
export type Shape = DerivedTypes.Shape;
export type Asset = DerivedTypes.Asset;
export type Lottie = Animation.Animation;
export type SolidColorLayer = Layers.SolidColorLayer;
export type Renderer = typeof Config.LOTTIE_RENDERER_CANVAS | typeof Config.LOTTIE_RENDERER_SVG;
export type Keyframe = AnimatedProperties.Keyframe;
